import { useCallback, useEffect, useState } from 'react';
import { useValidateAdslotMutation } from 'services/api/creativeServer';
import { track } from 'analytics/posthog';

export interface UseMosaicValidationReturn {
  validateError: string;
  progressview: boolean;
  progressStatus: React.ReactNode;
  handleValidateChange: (dimensionsOverride?: {
    height?: number;
    width?: number;
  }) => Promise<void>;
  reset: () => Promise<void>;
  validationStatus?: boolean;
  validateCheck: any;
  adslots?: string[];
  setValidationStatus: (value: boolean) => void;
  setValidationState: (state: {
    validateCheck?: any;
    validationStatus?: boolean;
    validateError?: string;
    mandatoryFields?: string;
  }) => void;
  mandatoryFields: string;
  setMandatoryFields: (value: string) => void;
}

type UseAdslotValidationArgs = {
  height: number | undefined;
  width: number | undefined;
  platform?: string;
  os?: string;
  analytics?: {
    surface?: string;
  };
};

export const useAdslotValidation = ({
  height,
  width,
  platform = 'adsure',
  os = '',
  analytics,
}: UseAdslotValidationArgs): UseMosaicValidationReturn => {
  const [mandatoryFields, setMandatoryFields] = useState('');
  const [validateError, setValidateError] = useState('');
  const [progressview, setProgressview] = useState(false);
  const [progressStatus, setProgressStatus] = useState<React.ReactNode>(
    'Validating Creative Metadata ...',
  );
  const [validationStatus, setValidationStatus] = useState(false);
  const [validateCheck, setValidateCheck] = useState<any>({});
  const [availableAdslots, setAvailableAdslots] = useState<string[]>([]);
  const [validateAdslot] = useValidateAdslotMutation();
  const shouldTrackPodValidationEvents = Boolean(analytics);
  const analyticsSurface = analytics?.surface || 'ondemand';

  useEffect(() => {
    const keys = Object.keys(validateCheck || {});
    const filteredKeys = keys.filter(el => el !== 'match');
    setAvailableAdslots(filteredKeys);
  }, [validateCheck]);

  const handleValidateChange = useCallback(
    async (dimensionsOverride?: { height?: number; width?: number }) => {
      const validationHeight = dimensionsOverride?.height || height;
      const validationWidth = dimensionsOverride?.width || width;

      setProgressview(true);
      setProgressStatus('Validating Creative Metadata ...');
      if (shouldTrackPodValidationEvents) {
        track('ondemand adslot validation requested', {
          adslot_candidate_count: availableAdslots.length,
          width: validationWidth,
          height: validationHeight,
          surface: analyticsSurface,
        });
      }
      try {
        const response = await validateAdslot({
          height: validationHeight,
          width: validationWidth,
          platform,
          os,
        }).unwrap();

        if (response?.message) {
          setProgressview(false);
          setValidateError(response?.message);
          setValidationStatus(false);
          setValidateCheck({});
          setMandatoryFields('');
          if (shouldTrackPodValidationEvents) {
            track('ondemand adslot validation completed', {
              status: 'failed',
              adslot_count: 0,
              mandatory_field_count: 0,
              width: validationWidth,
              height: validationHeight,
              surface: analyticsSurface,
            });
          }
          return;
        }
        setValidateCheck(response);
        setValidationStatus(true);
        setValidateError('');
        setProgressview(false);
        if (shouldTrackPodValidationEvents) {
          const adslotCount = Object.keys(response || {}).filter(
            key => key !== 'match',
          ).length;
          const mandatoryFieldCount = mandatoryFields
            ? mandatoryFields
                .split(',')
                .map(field => field.trim())
                .filter(Boolean).length
            : 0;
          track('ondemand adslot validation completed', {
            status: 'success',
            adslot_count: adslotCount,
            mandatory_field_count: mandatoryFieldCount,
            width: validationWidth,
            height: validationHeight,
            surface: analyticsSurface,
          });
        }
      } catch (error: any) {
        setProgressview(false);
        setValidateCheck({});

        const apiMessage =
          error?.data?.message ||
          error?.error ||
          'Validation failed. Unknown error';

        setValidateError(apiMessage);
        setValidationStatus(false);
        if (shouldTrackPodValidationEvents) {
          track('ondemand adslot validation completed', {
            status: 'failed',
            adslot_count: 0,
            mandatory_field_count: 0,
            width: validationWidth,
            height: validationHeight,
            surface: analyticsSurface,
          });
        }
      }
    },
    [
      analyticsSurface,
      availableAdslots.length,
      height,
      mandatoryFields,
      os,
      platform,
      shouldTrackPodValidationEvents,
      validateAdslot,
      width,
    ],
  );

  const reset = useCallback(async () => {
    setValidateCheck({});
    setValidationStatus(false);
    setValidateError('');
  }, []);

  const setValidationState = useCallback(
    ({
      validateCheck: nextValidateCheck = {},
      validationStatus: nextValidationStatus = false,
      validateError: nextValidateError = '',
      mandatoryFields: nextMandatoryFields = '',
    }) => {
      setValidateCheck(nextValidateCheck || {});
      setValidationStatus(Boolean(nextValidationStatus));
      setValidateError(nextValidateError || '');
      setMandatoryFields(nextMandatoryFields || '');
      setProgressview(false);
    },
    [],
  );

  return {
    validateCheck,
    adslots: availableAdslots,
    validateError,
    progressview,
    progressStatus,
    validationStatus,
    setValidationStatus,
    setValidationState,
    handleValidateChange,
    reset,
    mandatoryFields,
    setMandatoryFields,
  };
};
